import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { contentApi } from '../services/api';

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  schema?: object | object[];
  keywords?: string;
}

export function SEO({ 
  title, 
  description, 
  image = '/og-image.png', 
  url = window.location.href,
  type = 'website',
  schema,
  keywords
}: SEOProps) {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<any>(null);

  useEffect(() => {
    contentApi.getSettings('seo').then(data => {
      if (data && Object.keys(data).length > 0) {
        setSettings(data);
      }
    }).catch(err => console.error('Error loading SEO settings:', err));
  }, []);
  
  const siteName = 'AGP HUB';
  const siteTitle = settings?.siteTitle || 'AGP - El Marketplace del Césped Artificial';
  const fullTitle = title ? `${title} | ${siteName}` : siteTitle;
  const metaDescription = description || settings?.metaDescription || t('seo.defaultDescription', 'La plataforma líder para profesionales del césped artificial. Marketplace, directorio y herramientas de gestión.');
  const metaKeywords = keywords || settings?.keywords || 'césped artificial, instaladores, marketplace, b2b, b2c';
  
  // Default Organization Schema
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "AGP HUB",
    "url": "https://agphub.com",
    "logo": "https://agphub.com/logo.png",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+34-900-000-000",
      "contactType": "customer service",
      "areaServed": "EU",
      "availableLanguage": ["Spanish", "English", "French", "Portuguese", "Italian", "German", "Dutch"]
    },
    "sameAs": [
      "https://www.linkedin.com/groups/3671473/"
    ]
  };

  const finalSchemas = Array.isArray(schema) 
    ? [organizationSchema, ...schema] 
    : (schema ? [organizationSchema, schema] : [organizationSchema]);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      <link rel="canonical" href={url} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={siteName} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD Structured Data */}
      {finalSchemas.map((s, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}

      {/* Google Analytics */}
      {settings?.googleAnalyticsId && (
        <script async src={`https://www.googletagmanager.com/gtag/js?id=${settings.googleAnalyticsId}`}></script>
      )}
      {settings?.googleAnalyticsId && (
        <script>
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${settings.googleAnalyticsId}');
          `}
        </script>
      )}
    </Helmet>
  );
}

/**
 * Helper to generate Product Schema
 */
export function generateProductSchema(product: any, url: string, lang: string) {
  const getLocalizedValue = (val: any, l: string) => {
    if (!val) return '';
    if (typeof val === 'string') return val;
    return val[l] || val['es'] || Object.values(val)[0] || '';
  };

  const name = getLocalizedValue(product.name, lang);
  const description = getLocalizedValue(product.description, lang);
  const price = product.pvp || product.price || 0;

  return {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": name,
    "image": product.images || [],
    "description": description,
    "sku": product.id,
    "brand": {
      "@type": "Brand",
      "name": "AGP HUB"
    },
    "offers": {
      "@type": "Offer",
      "url": url,
      "priceCurrency": "EUR",
      "price": price,
      "availability": product.stockStatus === 'in_stock' ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "seller": {
        "@type": "Organization",
        "name": "AGP HUB"
      }
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": product.rating || 5,
      "reviewCount": product.reviews?.length || 1
    }
  };
}

/**
 * Helper to generate FAQ Schema
 */
export function generateFAQSchema(faqs: { question: string, answer: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer.replace(/<[^>]*>?/gm, '') // Strip HTML tags
      }
    }))
  };
}

/**
 * Helper to generate Professional/LocalBusiness Schema
 */
export function generateProfessionalSchema(pro: any, url: string, lang: string) {
  const getLocalizedValue = (val: any, l: string) => {
    if (!val) return '';
    if (typeof val === 'string') return val;
    return val[l] || val['es'] || Object.values(val)[0] || '';
  };

  const name = getLocalizedValue(pro.name, lang);
  const description = getLocalizedValue(pro.description, lang);
  const city = getLocalizedValue(pro.address?.city, lang);
  const country = getLocalizedValue(pro.address?.country, lang);

  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": name,
    "description": description,
    "url": url,
    "image": pro.logo || pro.banner || '',
    "address": {
      "@type": "PostalAddress",
      "addressLocality": city,
      "addressCountry": country,
      "postalCode": pro.address?.postalCode || ''
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": pro.rating || 5,
      "reviewCount": pro.reviews?.length || 1
    }
  };
}
